import { useState, useEffect, useMemo } from "react";
import { Button, Modal, Input, Spin, message } from "antd";
import { Plus, X } from "react-feather";
import styles from "./stylesTopLeaguesPerSport.module.css";
// Import AddNewIpPopup styles for consistent modal styling
import "../../../WhiteList/AddNewIpPopup/stylesAddNewIpPopup.css";
// Import API functions
import { API_ROUTES } from "../../../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  getApiErrorMessage,
  getApiSuccessMessage,
} from "../../../../utils/ErrorUtils";
import SortingTable from "../SortingTable/SortingTable";
import { useSportsbookFm } from "../SportsbookFmContext";
import { RenameEntityModal } from "../Translations/RenameEntityModal";
import { useProductFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { TRIVELTA_PRODUCTS } from "../../../../utils/products";

const TopLeaguesPerSport = () => {
  const { canEdit } = useSportsbookFm();
  const { isEnabled: translationsFlagEnabled } = useProductFeatureFlag(
    "fm_translations",
    TRIVELTA_PRODUCTS.SPORTSBOOK
  );
  const translationsEnabled = translationsFlagEnabled === true;
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeSport, setActiveSport] = useState(null);
  const activePreMatchLive = "PREMATCH";

  // Rename state
  const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameLoading, setRenameLoading] = useState(false);

  // Get token and create API instance
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken
  );

  const [popularTabs, setPopularTabs] = useState([]);
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [sports, setSports] = useState([]);
  const [sportsLoading, setSportsLoading] = useState(false);


  // API Functions
  const fetchSports = async () => {
    setSportsLoading(true);
    const params = {
      entity_type: activePreMatchLive,
    };
    try {
      const response = await PrivateAdminPanelInstance.post(API_ROUTES.GET_EVENT_MARKETS_SPORTS, { params });
      const sportsData = response.data.sports;
      
      setSports(sportsData);
      
      // Set the first sport as active
      if (sportsData?.length > 0) {
        setActiveSport(sportsData[0].sport_id);
      }
    } catch (error) {
      console.error("Error fetching sports:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    } finally {
      setSportsLoading(false);
    }
  };

  const fetchTopLeaguesPerSport = async () => {
    if (!activeSport) return;
    
    try {
      setIsTableLoading(true);
      const url = API_ROUTES.GET_TOP_LEAGUES_PER_SPORT
        .replace("{sport_id}", activeSport)
        .replace("{event_type}", activePreMatchLive);
      
      const response = await PrivateAdminPanelInstance.get(url);

      if (response.data.success) {
        const topMarketsData = response.data.data.items.map((item) => ({
          id: item?.entity_id || "",
          item_id: item?.item_id || "",
          ename: item?.display_name || "",
          order: item?.display_order?.toString() || "",
          is_deleted: item?.is_deleted ?? false,
          sport_id: item?.sport_id || "",
          event_type: item?.event_type || "",
          market_ids: item?.market_ids || [],
        }));

        setPopularTabs(topMarketsData);
      }
    } catch (error) {
      console.error("Error fetching top leagues per sport:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    } finally {
      setIsTableLoading(false);
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setIsAddModalVisible(true);
  };

  const handleModalClose = () => {
    setIsAddModalVisible(false);
    setSearchValue("");
    setSelectedItem(null);
    setSearchOptions([]);
  };

  // Real search function using API
  const fetchSearchOptions = useMemo(() => {
    let timer;
    return (query) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        if (!query || query.length < 2 || !activeSport) {
          setSearchOptions([]);
          return;
        }
        setSearchLoading(true);
        try {
          const url = API_ROUTES.AUTOCOMPLETE_TOP_LEAGUES_PER_SPORT
            .replace("{sport_id}", activeSport)
            .replace("{event_type}", activePreMatchLive)
            .replace("{search_term}", encodeURIComponent(query));

          const response = await PrivateAdminPanelInstance.get(url);

          if (response.data.success) {
            const searchData = response.data.data.map((market) => ({
              entity_id: market.entity_id,
              market_id: market.market_id,
              name: market.name,
              display_name: market.display_name,
              country_name: market.country_name,
            }));
            setSearchOptions(searchData);
          }
        } catch (error) {
          console.error("Error searching markets:", error);
          message.error(
            getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED)
          );
        } finally {
          setSearchLoading(false);
        }
      }, 300);
    };
  }, [activeSport, activePreMatchLive]);

  useEffect(() => {
    fetchSports();
  }, []);

  useEffect(() => {
    fetchTopLeaguesPerSport();
  }, [activeSport, activePreMatchLive]);

  useEffect(() => {
    fetchSearchOptions(searchValue);
  }, [searchValue, fetchSearchOptions]);

  const handleAddItemConfirm = async () => {
    if (!selectedItem || !activeSport) return;
    setAddLoading(true);
    
    try {
      const response = await PrivateAdminPanelInstance.post(
        API_ROUTES.CREATE_NEW_TOP_LEAGUES_PER_SPORT,
        {
          sport_id: activeSport,
          event_type: activePreMatchLive,
          item_id: selectedItem.entity_id.replace("LEAGUE#", ""),
          display_name: selectedItem.display_name,
          display_order: null,
          season_id: null
        }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );
      setIsAddModalVisible(false);
      setSearchValue("");
      setSelectedItem(null);
      setSearchOptions([]);

      // Refresh the top markets list
      await fetchTopLeaguesPerSport();
    } catch (error) {
      console.error("Error adding league:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    } finally {
      setAddLoading(false);
    }
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const toggleStatus = async (record) => {
    setIsTableLoading(true);

    const encodedId = encodeURIComponent(record.id);

    try {
      const response = await PrivateAdminPanelInstance.patch(
        API_ROUTES.SET_POPULAR_TAB_STATUS + "/" + encodedId + "/set-active",
        {
          is_active: !record.is_active,
        }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );

      // Update local state
      setPopularTabs((prevData) =>
        prevData.map((item) =>
          item.id === record.id
            ? { ...item, is_active: !item.is_active }
            : item
        )
      );
    } catch (error) {
      console.error("Error toggling item status:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    } finally {
      setIsTableLoading(false);
    }
  };

  const removeItem = async (record) => {
    setIsTableLoading(true);
    
    try {
      const response = await PrivateAdminPanelInstance.delete(
        API_ROUTES.DELETE_TOP_LEAGUES_PER_SPORT,
        { data: { entity_id: record.id } }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );

      // Update local state
      setPopularTabs((prevData) =>
        prevData.filter((item) => item.id !== record.id)
      );
    } catch (error) {
      console.error("Error deleting league:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    } finally {
      setIsTableLoading(false);
    }
  };

  const changeOrder = async (reorderedItems) => {
    setIsTableLoading(true);
    try {
      // Update local state first for immediate UI feedback
      setPopularTabs(reorderedItems);

      // Send API request to reorder
      const orderedIds = reorderedItems.map((item) => item.id);
      const response = await PrivateAdminPanelInstance.patch(
        API_ROUTES.ORDER_TOP_LEAGUES_PER_SPORT,
        {
          sport_id: activeSport,
          event_type: activePreMatchLive,
          ordered_ids: orderedIds,
        }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );
    } catch (error) {
      console.error("Error reordering top leagues:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
      // Revert to original state on error
      fetchTopLeaguesPerSport();
    } finally {
      setIsTableLoading(false);
    }
  };

  // Rename handlers
  const handleRenameClick = (item) => {
    setRenameTarget(item);
    setIsRenameModalVisible(true);
  };

  const handleRenameModalClose = () => {
    setIsRenameModalVisible(false);
    setRenameTarget(null);
  };

  const handleRenameConfirm = async (name) => {
    if (!renameTarget) return;
    setRenameLoading(true);
    try {
      const response = await PrivateAdminPanelInstance.patch(
        API_ROUTES.RENAME_TOP_LEAGUES_PER_SPORT,
        {
          entity_id: renameTarget.id,
          display_name: name,
        }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.ITEM_RENAMED_SUCCESSFULLY)
      );

      // Update local state
      setPopularTabs((prevData) =>
        prevData.map((item) =>
          item.id === renameTarget.id
            ? { ...item, ename: name }
            : item
        )
      );

      handleRenameModalClose();
    } catch (error) {
      console.error("Error renaming item:", error);
      message.error(getApiErrorMessage(error, AppStrings.FAILED_TO_RENAME_ITEM));
    } finally {
      setRenameLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.emSportsHeaderContainer}>
        <div className={styles.emSportsList}>
          {sportsLoading ? (
            <Spin size="small" />
          ) : (
            sports.map((s) => (
              <div
                key={s.sport_id}
                className={`${styles.emSportPill} ${activeSport === s.sport_id ? styles.active : ""
                  }`}
                onClick={() => setActiveSport(s.sport_id)}
              >
                <span>{s.name}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <div className={styles.header}>
        <h2>{AppStrings.TOP_LEAGUES_PER_SPORT}</h2>
        {canEdit && (
          <Button
            type="primary"
            icon={<Plus size={16} />}
            onClick={handleAddItem}
            className={styles.addButton}
          >
            {AppStrings.ADD_LEAGUE || "Add League"}
          </Button>
        )}
      </div>
      <div className={styles.tableContainer}>
        <SortingTable
          columns={[
            "",
            "#Order",
            "Name",
            "Markets", 
            "Action",
          ]}
          data={popularTabs}
          setData={setPopularTabs}
          toggleStatus={toggleStatus}
          removeItem={removeItem}
          onRename={handleRenameClick}
          changeOrder={changeOrder}
          isLoading={isTableLoading}
          canEdit={canEdit}
        />
      </div>

      <Modal
        closable={false}
        open={isAddModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        className="add-league-modal"
      >
        <div className="add-new-ip-panel">
          <div className="modal-header">
            <div className="modal-header-title">
              <h2 className="modal-title">{AppStrings.ADD_LEAGUE || "Add League"}</h2>
            </div>
            <X onClick={handleModalClose} className="close-icon" />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <div className="autocomplete">
              <Input
                placeholder={AppStrings.SEARCH_LEAGUES || "Search leagues..."}
                value={searchValue}
                onChange={handleSearchChange}
                disabled={!canEdit}
              />
              {searchValue ? (
                <div>
                  {searchLoading ? (
                    <div className="center">
                      <Spin size="small" />
                    </div>
                  ) : (
                    searchOptions?.map((option) => (
                      <div
                        key={option.entity_id}
                        className={`${styles.leagueCard} ${selectedItem?.entity_id === option.entity_id
                          ? styles.selected
                          : ""
                          }`}
                        onClick={canEdit ? () => handleItemSelect(option) : undefined}
                        style={{ cursor: canEdit ? "pointer" : "not-allowed" }}
                      >
                        <div className={styles.leagueInfo}>
                          <span className={styles.leagueName}>
                            {option.display_name}
                          </span>
                        </div>
                        {option.country_name && (
                          <div className={styles.countryTag}>
                            <span className={styles.countryName}>
                              {option.country_name}
                            </span>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                  {!searchLoading &&
                    searchOptions.length === 0 &&
                    searchValue && (
                      <div className="empty-mini">
                        {AppStrings.NO_RESULTS_FOUND || "No results found"}
                      </div>
                    )}
                </div>
              ) : null}
            </div>
          </div>
          <div className="modal-footer">
            <button
              className="cancel-button"
              onClick={handleModalClose}
              disabled={addLoading || !canEdit}
            >
              {AppStrings.CLOSE}
            </button>
            <button
              className={`submit-button ${!selectedItem || addLoading || !canEdit ? "disabled" : ""
                }`}
              disabled={!selectedItem || addLoading || !canEdit}
              onClick={handleAddItemConfirm}
            >
              {addLoading ? AppStrings.ADDING : AppStrings.ADD_LEAGUE}
            </button>
          </div>
        </div>
      </Modal>

      <RenameEntityModal
        open={isRenameModalVisible}
        title={AppStrings.RENAME_TOP_LEAGUE}
        nameLabel={AppStrings.DISPLAY_NAME}
        initialName={renameTarget?.ename ?? ""}
        entityKey={renameTarget?.id}
        translationsEnabled={translationsEnabled}
        saving={renameLoading}
        onSave={handleRenameConfirm}
        onClose={handleRenameModalClose}
      />
    </div>
  );
};

export default TopLeaguesPerSport;
